import { useEffect, useRef, useState, type ReactNode } from 'react';
import { ConnectionsPanel, type McpConnection } from './ConnectionsPanel';
import { GitKeyPanel } from './GitKeyPanel';
import { MyArtifactsPanel } from './MyArtifacts';
import { ReposPanel } from './ReposPanel';
import { FilesIcon, KeyIcon, PlugIcon, SignOutIcon, SparkleIcon } from './icons';

/**
 * One place for what is the person's own rather than the room's: their
 * artifacts, their Bitbucket key, their connections. Tabs on the left, the
 * page on the right, and signing out pinned to the bottom of the tab column.
 *
 * Every panel stays mounted while the dialog is open and the inactive one is
 * only hidden. Switching tabs must not throw away a half-pasted key or stop
 * watching an OAuth popup that is still waiting for the person to approve.
 */

export type SettingsTab = 'artifacts' | 'bitbucket' | 'connections' | 'repos';

interface Props {
  initialTab?: SettingsTab;
  onClose: () => void;
  /** Changes whenever the room on screen publishes or deletes; the artifact list re-reads then. */
  artifactsKey: unknown;
  onOpenArtifactInRoom: (roomId: string, artifactId: string) => void;
  onError: (message: string) => void;
  onConnectionsChange?: (connections: McpConnection[]) => void;
  /** Same marks the rail shows, so the tab that needs attention says so. */
  gitKeyMissing?: boolean;
  connectionBroken?: boolean;
}

export function SettingsDialog({
  initialTab = 'artifacts',
  onClose,
  artifactsKey,
  onOpenArtifactInRoom,
  onError,
  onConnectionsChange,
  gitKeyMissing,
  connectionBroken,
}: Props) {
  const [tab, setTab] = useState<SettingsTab>(initialTab);
  /** An open artifact needs more room than a settings page does. */
  const [viewing, setViewing] = useState(false);
  const closeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    closeRef.current?.focus();
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const tabs: { id: SettingsTab; label: string; icon: ReactNode; alert?: boolean }[] = [
    { id: 'artifacts', label: 'My artifacts', icon: <SparkleIcon size={15} /> },
    { id: 'bitbucket', label: 'Bitbucket', icon: <KeyIcon size={15} />, alert: gitKeyMissing },
    { id: 'connections', label: 'Bağlantılar', icon: <PlugIcon size={15} />, alert: connectionBroken },
    { id: 'repos', label: 'Repolar', icon: <FilesIcon size={15} /> },
  ];

  return (
    <div className="scrim" role="dialog" aria-modal="true" aria-label="Ayarlar">
      <div className={`share settings${viewing && tab === 'artifacts' ? ' settings--wide' : ''}`}>
        <nav className="settings__tabs" role="tablist" aria-orientation="vertical">
          <div className="settings__heading">Ayarlar</div>
          {tabs.map((t) => (
            <button
              key={t.id}
              role="tab"
              id={`settings-tab-${t.id}`}
              aria-selected={tab === t.id}
              aria-controls={`settings-pane-${t.id}`}
              className="settings__tab"
              onClick={() => setTab(t.id)}
            >
              {t.icon}
              <span>{t.label}</span>
              {t.alert && <span className="settings__dot" aria-label="dikkat gerekiyor" />}
            </button>
          ))}
          <a className="settings__tab settings__tab--signout" href="/auth/logout">
            <SignOutIcon size={15} />
            <span>Çıkış yap</span>
          </a>
        </nav>

        <div className="settings__pane">
          <button className="share__close settings__close" ref={closeRef} onClick={onClose} aria-label="Kapat">
            ×
          </button>
          <section
            role="tabpanel"
            id="settings-pane-artifacts"
            aria-labelledby="settings-tab-artifacts"
            className="settings__panel"
            hidden={tab !== 'artifacts'}
          >
            <MyArtifactsPanel
              refreshKey={artifactsKey}
              onOpenInRoom={onOpenArtifactInRoom}
              onError={onError}
              onViewing={setViewing}
            />
          </section>
          <section
            role="tabpanel"
            id="settings-pane-bitbucket"
            aria-labelledby="settings-tab-bitbucket"
            className="settings__panel"
            hidden={tab !== 'bitbucket'}
          >
            <GitKeyPanel />
          </section>
          <section
            role="tabpanel"
            id="settings-pane-connections"
            aria-labelledby="settings-tab-connections"
            className="settings__panel"
            hidden={tab !== 'connections'}
          >
            <ConnectionsPanel onChange={onConnectionsChange} />
          </section>
          <section
            role="tabpanel"
            id="settings-pane-repos"
            aria-labelledby="settings-tab-repos"
            className="settings__panel"
            hidden={tab !== 'repos'}
          >
            <ReposPanel />
          </section>
        </div>
      </div>
    </div>
  );
}
